import {Alert, FormField, useFormContext, type FormControl} from 'platform/components';
import {Grid, Text, VStack, Show} from 'platform/foundation';

import {useState} from 'react';
import {type FieldPath} from 'react-hook-form';

import {SelectableCard} from '@dms/features/new-vehicle';

import {
  resolveMakeIdentity,
  resolveModelIdentity,
  type CatalogueIdentity,
} from './catalogueIdentity';
import {t} from './catalogueUi';
import {
  draftCatalogueIdentity,
  draftMakeVehicleDataKey,
  existingDraftMake,
  existingDraftModel,
  withDraftMake,
  type NewModelDraft,
} from './newModelDraft';
import {
  optionLabel,
  useVehicleCatalogueMakeOptions,
  useVehicleCatalogueModelOptions,
} from './useVehicleCatalogueOptions';

export type NewModelFormValues = {draft: NewModelDraft};
export type CatalogueChoice = {
  id: string;
  name: string;
  vehicleDataKey?: string | null;
  archived?: boolean;
};
export type ModelChoice = CatalogueChoice & {makeId: string};

type Entity = 'make' | 'model';
/** Why the draft now points at an existing record, so the author sees what happened to the pick. */
type Reuse = {name: string; isLinked: boolean};

/** Keep what the author typed; replace only an empty name or the previous pick's label. */
const pickedName = (current: string, previousLabel: string | undefined, label: string) =>
  !current.trim() || current === previousLabel ? label : current;

const reuseMessage = (entity: Entity, reuse: Reuse) =>
  entity === 'make' // eslint-disable-line no-nested-ternary -- Pre-existing violation
    ? reuse.isLinked
      ? t(
          'entity.newVehicle.authoring.makeLinkedOnCreate',
          '{name} is used as the make and gets linked to the vehicle catalogue when you create the draft. No new make is created.',
          {name: reuse.name}
        )
      : t(
          'entity.newVehicle.authoring.makeReused',
          '{name} is already in the catalogue, so the model is added to it instead of creating the make again.',
          {name: reuse.name}
        )
    : reuse.isLinked
      ? t(
          'entity.newVehicle.authoring.modelLinkedOnCreate',
          '{name} is used as the model and gets linked to the vehicle catalogue when you create the draft. No new model is created.',
          {name: reuse.name}
        )
      : t(
          'entity.newVehicle.authoring.modelReused',
          '{name} is already a model of this make, so the model year is added to it instead of creating the model again.',
          {name: reuse.name}
        );

/**
 * What an existing record means for the make or model being created: an exact match to use or
 * link (a duplicate would be refused), or look-alikes the author has to rule on. Archived matches
 * and taken keys only block; the footer explains them.
 */
function IdentityDecision(props: {
  entity: Entity;
  identity: CatalogueIdentity<CatalogueChoice>;
  pickedKey: string;
  pickedLabel: string | undefined;
  keepSeparate: boolean;
  isLocked: boolean;
  onUse: (record: CatalogueChoice, linkKey: string) => void;
  onKeepSeparate: () => void;
}) {
  const {match} = props.identity;
  const actions = (records: CatalogueChoice[], link: boolean) =>
    props.isLocked
      ? []
      : records.slice(0, 3).map((record) => ({
          variant: 'secondary' as const,
          title: link
            ? t('entity.newVehicle.authoring.linkExisting', 'Link {name}', {name: record.name})
            : t('entity.newVehicle.authoring.useExisting', 'Use {name}', {name: record.name}),
          onClick: () => props.onUse(record, link ? props.pickedKey : ''),
        }));
  if (match && match.kind !== 'archived') {
    const isLink = match.kind === 'link';
    return (
      <Alert
        layout="detailed"
        variant="warning"
        data-testid={`new-model-wizard-${props.entity}-existing`}
        message={
          isLink
            ? t(
                'entity.newVehicle.authoring.linkExistingQuestion',
                '{name} already exists without a vehicle catalogue link. Link it to {label} instead of creating a duplicate?',
                {name: match.record.name, label: props.pickedLabel ?? props.pickedKey}
              )
            : t(
                'entity.newVehicle.authoring.useExistingQuestion',
                '{name} already exists and a second one would be refused. Continue with {name}?',
                {name: match.record.name}
              )
        }
        actions={actions([match.record], isLink)}
      />
    );
  }
  if (!props.identity.similar.length || !props.pickedKey) {
    return null;
  }
  if (props.keepSeparate) {
    return (
      <Alert
        variant="info"
        message={
          props.entity === 'make'
            ? t(
                'entity.newVehicle.authoring.newMakeBesideSimilar',
                'A new make is created next to {name}.',
                {name: props.identity.similar[0].name}
              )
            : t(
                'entity.newVehicle.authoring.newModelBesideSimilar',
                'A new model is created next to {name}.',
                {name: props.identity.similar[0].name}
              )
        }
      />
    );
  }
  return (
    <Alert
      layout="detailed"
      variant="warning"
      data-testid={`new-model-wizard-${props.entity}-similar`}
      message={t(
        'entity.newVehicle.authoring.similarQuestion',
        'Is {name} the same as {label} in the vehicle catalogue? Link it, or create a new one.',
        {
          name: props.identity.similar.map((record) => record.name).join(', '),
          label: props.pickedLabel ?? props.pickedKey,
        }
      )}
      actions={[
        ...actions(props.identity.similar, true),
        ...(props.isLocked
          ? []
          : [
              {
                variant: 'secondary' as const,
                title:
                  props.entity === 'make'
                    ? t('entity.newVehicle.authoring.createNewMake', 'Create new make')
                    : t('entity.newVehicle.authoring.createNewModel', 'Create new model'),
                onClick: props.onKeepSeparate,
              },
            ]),
      ]}
    />
  );
}

function CatalogueKeyField(props: {
  control: FormControl<NewModelFormValues>;
  name: 'draft.make.vehicleDataKey' | 'draft.model.vehicleDataKey';
  label: string;
  helperText: string;
  catalogue: ReturnType<typeof useVehicleCatalogueMakeOptions>;
  isDisabled: boolean;
  onPick: (key: string, label: string | undefined) => void;
}) {
  return (
    <FormField
      control={props.control}
      name={props.name}
      type="choice"
      isSearchable
      isNotClearable
      menuInPortal
      isDisabled={props.isDisabled}
      isLoading={props.catalogue.isLoading}
      label={props.label}
      helperText={props.helperText}
      options={props.catalogue.options}
      onChange={(value) => {
        const key = typeof value === 'string' ? value : '';
        props.onPick(key, optionLabel(props.catalogue.options, key));
      }}
    />
  );
}

const catalogueUnavailable = () =>
  t(
    'entity.newVehicle.catalogue.vehicleCatalogueUnavailable',
    "The vehicle catalogue can't be loaded right now. You can continue without a link."
  );

// eslint-disable-next-line max-lines-per-function -- Pre-existing violation
export function NewModelIdentitySteps(props: {
  step: 1 | 2;
  draft: NewModelDraft;
  isLocked: boolean;
  control: FormControl<NewModelFormValues>;
  makes: CatalogueChoice[];
  models: ModelChoice[];
  update: (next: NewModelDraft) => void;
  text: (
    name: FieldPath<NewModelFormValues>,
    key: string,
    label: string,
    required?: boolean
  ) => React.ReactNode;
}) {
  const {draft, update} = props;
  const form = useFormContext<NewModelFormValues>();
  const [reused, setReused] = useState<{make?: Reuse; model?: Reuse}>({});
  const makeCatalogue = useVehicleCatalogueMakeOptions();
  const makeKey = draftMakeVehicleDataKey(draft, props.makes);
  const modelCatalogue = useVehicleCatalogueModelOptions(props.step === 2 ? makeKey : null);
  const identity = draftCatalogueIdentity(draft, props.makes, props.models);
  const activeMakes = props.makes.filter((make) => !make.archived);
  const chosenMake = props.makes.find((make) => make.id === draft.make.id);
  const makeModels = props.models.filter(
    (model) => model.makeId === draft.make.id && !model.archived
  );
  const chosenModel = makeModels.find((model) => model.id === draft.model.id);

  const chooseMake = (record: CatalogueChoice, linkKey: string) => {
    setReused({make: {name: record.name, isLinked: Boolean(linkKey)}});
    update(
      withDraftMake(form.getValues('draft'), existingDraftMake(record.id, linkKey), {mode: 'new'})
    );
  };
  const chooseModel = (record: CatalogueChoice, linkKey: string) => {
    const current = form.getValues('draft');
    setReused(({make}) => ({make, model: {name: record.name, isLinked: Boolean(linkKey)}}));
    update({...current, model: existingDraftModel(current, record.id, linkKey)});
  };

  const pickMake = (key: string, label: string | undefined) => {
    const current = form.getValues('draft');
    if (current.make.mode === 'existing') {
      // Linking the chosen make: its models' catalogue keys must follow the new make key.
      update(
        withDraftMake(
          current,
          {...current.make, vehicleDataKey: key},
          current.model.mode === 'existing' ? {mode: 'existing', id: current.model.id} : {}
        )
      );
      return;
    }
    // A make already linked to this key is exactly what the author means; nothing is written.
    const exact = key
      ? resolveMakeIdentity(props.makes, {name: '', vehicleDataKey: key}).match
      : undefined;
    if (exact?.kind === 'key') {
      chooseMake(exact.record, '');
      return;
    }
    const previousLabel = optionLabel(makeCatalogue.options, current.make.vehicleDataKey);
    update(
      withDraftMake(current, {
        ...current.make,
        vehicleDataKey: key,
        keepSeparate: false,
        name:
          key && label ? pickedName(current.make.name, previousLabel, label) : current.make.name,
      })
    );
  };
  const pickModel = (key: string, label: string | undefined) => {
    const current = form.getValues('draft');
    if (current.model.mode === 'existing') {
      update({...current, model: {...current.model, vehicleDataKey: key}});
      return;
    }
    const exact =
      key && current.make.mode === 'existing'
        ? resolveModelIdentity(props.models, current.make.id, {name: '', vehicleDataKey: key}).match
        : undefined;
    if (exact?.kind === 'key') {
      chooseModel(exact.record, '');
      return;
    }
    const previousLabel = optionLabel(modelCatalogue.options, current.model.vehicleDataKey);
    update({
      ...current,
      model: {
        ...current.model,
        vehicleDataKey: key,
        keepSeparate: false,
        name:
          key && label ? pickedName(current.model.name, previousLabel, label) : current.model.name,
      },
    });
  };

  const makeKeyField = (isLinking: boolean) => (
    <CatalogueKeyField
      control={props.control}
      name="draft.make.vehicleDataKey"
      label={
        isLinking
          ? t(
              'entity.newVehicle.catalogue.linkVehicleCatalogueMake',
              'Link to vehicle catalogue make'
            )
          : t('entity.newVehicle.catalogue.vehicleCatalogueMake', 'Vehicle catalogue make')
      }
      helperText={
        makeCatalogue.isError // eslint-disable-line no-nested-ternary -- Pre-existing violation
          ? catalogueUnavailable()
          : isLinking
            ? t(
                'entity.newVehicle.catalogue.linkMakeHelper',
                'Optional. Linking lets ordered vehicles carry the official make and its models be linked too.'
              )
            : t(
                'entity.newVehicle.catalogue.makeCatalogueHelper',
                'Link the make so ordered vehicles carry the official make. An existing make with the same link or name is offered instead of a new one.'
              )
      }
      catalogue={makeCatalogue}
      isDisabled={props.isLocked}
      onPick={pickMake}
    />
  );
  const modelKeyField = (isLinking: boolean) => (
    <CatalogueKeyField
      control={props.control}
      name="draft.model.vehicleDataKey"
      label={
        isLinking
          ? t(
              'entity.newVehicle.catalogue.linkVehicleCatalogueModel',
              'Link to vehicle catalogue model'
            )
          : t('entity.newVehicle.catalogue.vehicleCatalogueModel', 'Vehicle catalogue model')
      }
      helperText={
        !makeKey // eslint-disable-line no-nested-ternary -- Pre-existing violation
          ? t(
              'entity.newVehicle.catalogue.modelKeyNeedsMakeKey',
              'Available once the make is linked to the vehicle catalogue.'
            )
          : modelCatalogue.isError
            ? catalogueUnavailable()
            : t(
                'entity.newVehicle.catalogue.modelCatalogueHelper',
                "Only models of the make's catalogue entry are offered. An existing model with the same link or name is offered instead of a new one."
              )
      }
      catalogue={modelCatalogue}
      isDisabled={props.isLocked || !makeKey}
      onPick={pickModel}
    />
  );

  if (props.step === 1) {
    return (
      <VStack spacing={5}>
        <VStack spacing={2}>
          <Text size="small">{t('entity.newVehicle.authoring.makeSource', 'Make source')}</Text>
          <Grid columns={[1, 1, 2]} spacing={3}>
            <SelectableCard
              control="radio"
              name="makeSource"
              title={t('entity.newVehicle.authoring.existingMake', 'Existing make')}
              description={t(
                'entity.newVehicle.authoring.existingMakeDescription',
                'Pick from the catalogue.'
              )}
              isSelected={draft.make.mode === 'existing'}
              isLocked={props.isLocked}
              onSelect={() => {
                setReused({});
                update(withDraftMake(draft, existingDraftMake(draft.make.id), {mode: 'new'}));
              }}
            />
            <SelectableCard
              control="radio"
              name="makeSource"
              title={t('entity.newVehicle.authoring.createNewMake', 'Create new make')}
              description={t(
                'entity.newVehicle.authoring.createNewMakeDescription',
                'Enter the make name and HSN.'
              )}
              isSelected={draft.make.mode === 'new'}
              isLocked={props.isLocked}
              onSelect={() => {
                setReused({});
                update(
                  withDraftMake(
                    draft,
                    {...draft.make, mode: 'new', vehicleDataKey: ''},
                    {mode: 'new'}
                  )
                );
              }}
            />
          </Grid>
        </VStack>
        <Show when={draft.make.mode === 'existing' && reused.make}>
          {(make) => (
            <Alert
              variant="info"
              data-testid="new-model-wizard-make-reused"
              message={reuseMessage('make', make)}
            />
          )}
        </Show>
        <Show when={draft.make.mode === 'new'}>
          <IdentityDecision
            entity="make"
            identity={identity.make}
            pickedKey={draft.make.vehicleDataKey}
            pickedLabel={optionLabel(makeCatalogue.options, draft.make.vehicleDataKey)}
            keepSeparate={draft.make.keepSeparate}
            isLocked={props.isLocked}
            onUse={chooseMake}
            onKeepSeparate={() => update({...draft, make: {...draft.make, keepSeparate: true}})}
          />
        </Show>
        {draft.make.mode === 'existing' ? (
          <Grid columns={[1, 1, 2]} spacing={4}>
            <FormField
              control={props.control}
              name="draft.make.id"
              isDisabled={props.isLocked}
              type="choice"
              isSearchable
              isNotClearable
              label={t('entity.vehicle.labels.make', 'Make')}
              placeholder={t('entity.newVehicle.authoring.searchMakes', 'Search makes…')}
              isRequired
              options={activeMakes.map((row) => ({value: row.id, label: row.name}))}
              onChange={() => {
                setReused({});
                const current = form.getValues('draft');
                update(withDraftMake(current, existingDraftMake(current.make.id)));
              }}
            />
            {chosenMake && !chosenMake.vehicleDataKey ? makeKeyField(true) : null}
          </Grid>
        ) : (
          <Grid columns={[1, 1, 2]} spacing={4}>
            {makeKeyField(false)}
            {props.text('draft.make.name', 'entity.newVehicle.catalogue.makeName', 'Name', true)}
            <FormField
              control={props.control}
              name="draft.make.hsn"
              isDisabled={props.isLocked}
              type="text"
              label={t('entity.newVehicle.authoring.hsn', 'HSN')}
              helperText={t(
                'entity.newVehicle.authoring.hsnHint',
                'Manufacturer key number (HSN), optional'
              )}
            />
          </Grid>
        )}
      </VStack>
    );
  }

  return (
    <VStack spacing={5}>
      <VStack spacing={2}>
        <Text size="small">{t('entity.newVehicle.authoring.modelSource', 'Model source')}</Text>
        <Grid columns={[1, 1, 2]} spacing={3}>
          <SelectableCard
            control="radio"
            name="modelSource"
            title={t('entity.newVehicle.authoring.existingModel', 'Existing model')}
            description={t(
              'entity.newVehicle.authoring.existingModelDescription',
              'Add a model year to a catalogue model.'
            )}
            isSelected={draft.model.mode === 'existing'}
            isDisabled={draft.make.mode !== 'existing'}
            isLocked={props.isLocked}
            reason={
              draft.make.mode !== 'existing'
                ? t(
                    'entity.newVehicle.authoring.existingModelNeedsExistingMake',
                    'Choose an existing make in Step 1 to use a catalogue model.'
                  )
                : undefined
            }
            footer={
              draft.make.mode !== 'existing' ? (
                <Text size="small" color="secondary">
                  {t(
                    'entity.newVehicle.authoring.existingModelNeedsExistingMake',
                    'Choose an existing make in Step 1 to use a catalogue model.'
                  )}
                </Text>
              ) : undefined
            }
            onSelect={() => {
              setReused(({make}) => ({make}));
              update({...draft, model: existingDraftModel(draft, draft.model.id)});
            }}
          />
          <SelectableCard
            control="radio"
            name="modelSource"
            title={t('entity.newVehicle.authoring.createNewModel', 'Create new model')}
            description={t(
              'entity.newVehicle.authoring.createNewModelDescription',
              'Enter the model identity and type keys.'
            )}
            isSelected={draft.model.mode === 'new'}
            isLocked={props.isLocked}
            onSelect={() => {
              setReused(({make}) => ({make}));
              update({...draft, model: {...draft.model, mode: 'new', id: '', vehicleDataKey: ''}});
            }}
          />
        </Grid>
      </VStack>
      <Show when={draft.model.mode === 'existing' && reused.model}>
        {(model) => (
          <Alert
            variant="info"
            data-testid="new-model-wizard-model-reused"
            message={reuseMessage('model', model)}
          />
        )}
      </Show>
      <Show when={draft.model.mode === 'new'}>
        <IdentityDecision
          entity="model"
          identity={identity.model}
          pickedKey={draft.model.vehicleDataKey}
          pickedLabel={optionLabel(modelCatalogue.options, draft.model.vehicleDataKey)}
          keepSeparate={draft.model.keepSeparate}
          isLocked={props.isLocked}
          onUse={chooseModel}
          onKeepSeparate={() => update({...draft, model: {...draft.model, keepSeparate: true}})}
        />
      </Show>
      {draft.model.mode === 'existing' && draft.make.mode === 'existing' ? (
        <Grid columns={[1, 1, 2]} spacing={4}>
          <FormField
            control={props.control}
            name="draft.model.id"
            isDisabled={props.isLocked}
            type="choice"
            isSearchable
            isNotClearable
            label={t('entity.vehicle.labels.model', 'Model')}
            placeholder={t('entity.newVehicle.authoring.searchModels', 'Search models…')}
            isRequired
            options={makeModels.map((row) => ({value: row.id, label: row.name}))}
            onChange={() => {
              setReused(({make}) => ({make}));
              const current = form.getValues('draft');
              update({...current, model: existingDraftModel(current, current.model.id)});
            }}
          />
          {chosenModel && !chosenModel.vehicleDataKey ? modelKeyField(true) : null}
        </Grid>
      ) : (
        <Grid columns={[1, 1, 2]} spacing={4}>
          {modelKeyField(false)}
          {props.text(
            'draft.model.name',
            'entity.newVehicle.catalogue.modelName',
            'Model name',
            true
          )}
          {props.text('draft.model.tsn', 'entity.newVehicle.authoring.tsn', 'Type key (TSN)')}
          {props.text(
            'draft.model.modelFamily',
            'entity.newVehicle.authoring.familyGroup',
            'Family group'
          )}
        </Grid>
      )}
    </VStack>
  );
}
